import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { Badge, Popover, Progress, Tooltip } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "stores"
import { useProcessMonitorStore } from "stores/process-monitor"
import {
  restoreTransactionTracking,
  stopTransactionTracking,
} from "./transactionProgressClient"
import { installBulkProgressInterceptors } from "./bulkProgressInterceptors"
import ProcessMonitorIcon from "./ProcessMonitorIcon"
import ProcessProgressModal from "./ProcessProgressModal"
import {
  ProcessExternalLinkIcon,
  ProcessTimeIcon,
} from "./ProcessStatusIcons"
import { getProcessDestination } from "./processRegistry"
import {
  getDisplayedProgress,
  getProcessActivityTime,
  getTransactionKey,
  hasReportedProgress,
  isActiveTransaction,
  isTerminalTransaction,
  TransactionProgress,
} from "./types"

dayjs.extend(relativeTime)

const sortByUpdatedAt = (processes: TransactionProgress[]) =>
  [...processes].sort(
    (first, second) =>
      new Date(getProcessActivityTime(second)).getTime() -
      new Date(getProcessActivityTime(first)).getTime()
  )

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    connecting: "Connecting",
    queued: "Queued",
    running: "In progress",
    processing: "In progress",
    completed: "Completed",
    completed_with_errors: "Completed with errors",
    stopped: "Stopped",
    failed: "Failed",
  }

  return labels[status.toLowerCase()] || status.replace(/_/g, " ")
}

const getStatusStyle = (status: string) => {
  if (status === "completed") {
    return {
      icon: CheckCircleIcon,
      iconClass: "text-success500",
      progressColor: "#3a940a",
    }
  }

  if (
    status === "failed" ||
    status === "completed_with_errors" ||
    status === "stopped"
  ) {
    return {
      icon: ExclamationCircleIcon,
      iconClass: "text-danger500",
      progressColor: "#f41418",
    }
  }

  return {
    icon: ProcessTimeIcon,
    iconClass: "text-[#62748E]",
    progressColor: "rgb(var(--color-primary))",
  }
}

const ProcessItem = ({ process }: { process: TransactionProgress }) => {
  const navigate = useNavigate()
  const setOpen = useProcessMonitorStore((state) => state.setOpen)
  const normalizedStatus = process.status.toLowerCase()
  const statusStyle = getStatusStyle(normalizedStatus)
  const StatusIcon = statusStyle.icon
  const isActive = isActiveTransaction(normalizedStatus)
  const progress = getDisplayedProgress(process)
  const hasProgress = hasReportedProgress(process)
  const isIndeterminate = isActive && !hasProgress

  return (
    <article className="border-b border-neutral30 px-5 py-4 last:border-b-0">
      <h4 className="truncate text-sm font-semibold text-neutral700">
        {process.title || `${process.type} process`}
      </h4>

      <p className="mt-1 truncate text-xs leading-5 text-neutral300">
        {process.message}
      </p>

      {hasProgress && (
        <Progress
          className="mt-2"
          percent={progress}
          showInfo={false}
          size="small"
          status={isActive ? "active" : "normal"}
          strokeColor={statusStyle.progressColor}
          trailColor="#edf0f2"
        />
      )}

      {isIndeterminate && (
        <div
          className="process-monitor-indeterminate mt-2"
          role="progressbar"
          aria-label={`${process.title || process.type} is in progress`}
        >
          <span aria-hidden="true" />
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-3">
        <span
          className={`flex min-w-0 items-center gap-1.5 text-xs ${statusStyle.iconClass}`}
        >
          <StatusIcon
            className="h-3 w-3 shrink-0"
          />
          <span>{getStatusLabel(normalizedStatus)}</span>
          {isActive && hasProgress && <span aria-hidden="true">•</span>}
          {isActive ? (
            hasProgress && <span>{progress}%</span>
          ) : (
            <span>{dayjs(getProcessActivityTime(process)).fromNow()}</span>
          )}
        </span>

        {getProcessDestination(process) && (
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              const destination = getProcessDestination(process)
              if (destination) navigate(destination)
            }}
            className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary500 transition-colors hover:text-primary700"
          >
            View
            <ProcessExternalLinkIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    </article>
  )
}

const ProcessSectionHeader = ({
  label,
  count,
  isOpen,
  onToggle,
}: {
  label: string
  count: number
  isOpen: boolean
  onToggle: () => void
}) => (
  <button
    type="button"
    aria-expanded={isOpen}
    onClick={onToggle}
    className="sticky top-0 z-10 flex w-full items-center justify-between border-y border-neutral30 bg-neutral20 px-5 py-2.5 text-left"
  >
    <span className="flex items-center gap-2 text-xs font-medium text-neutral300">
      {label}
      <span className="rounded-full bg-neutral30 px-2 py-0.5 text-[11px] font-semibold leading-none text-neutral300">
        {count}
      </span>
    </span>
    <ChevronUpIcon
      className={`h-4 w-4 text-neutral300 transition-transform duration-200 ${
        isOpen ? "" : "rotate-180"
      }`}
    />
  </button>
)

const ProcessActivityContent = () => {
  const processes = useProcessMonitorStore((state) => state.processes)
  const setOpen = useProcessMonitorStore((state) => state.setOpen)
  const [isRunningOpen, setIsRunningOpen] = useState(true)
  const [isRecentOpen, setIsRecentOpen] = useState(true)

  const { activeProcesses, recentProcesses } = useMemo(() => {
    const values = sortByUpdatedAt(Object.values(processes))
    return {
      activeProcesses: values.filter((process) =>
        isActiveTransaction(process.status)
      ),
      recentProcesses: values.filter((process) =>
        isTerminalTransaction(process.status)
      ),
    }
  }, [processes])

  return (
    <div className="flex max-h-[min(700px,calc(100vh-96px))] w-[min(440px,calc(100vw-24px))] flex-col overflow-hidden bg-white font-avenir">
      <header className="flex shrink-0 items-center justify-between border-b border-neutral30 px-5 py-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral700">
          <ProcessMonitorIcon className="h-3 w-3" />
          Process Monitor
        </h3>
        <Tooltip title="Close">
          <button
            type="button"
            aria-label="Close process activity"
            onClick={() => setOpen(false)}
            className="flex h-8 w-8 items-center justify-center text-neutral300 transition-colors hover:text-neutral600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </Tooltip>
      </header>

      <p className="shrink-0 border-b border-neutral30 px-5 py-4 text-sm text-neutral300">
        Background tasks and recent activity
      </p>

      <div className="min-h-0 overflow-y-auto overscroll-contain">
        {!activeProcesses.length && !recentProcesses.length ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center px-8 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-[6px] bg-neutral20 text-neutral300">
              <ArrowPathIcon className="h-6 w-6" />
            </div>
            <p className="mt-4 text-sm font-semibold text-neutral600">
              Nothing to display
            </p>
            <p className="mt-1 max-w-[260px] text-xs leading-5 text-neutral300">
              Company-wide long running processes will appear here
            </p>
          </div>
        ) : (
          <>
            {activeProcesses.length > 0 && (
              <section>
                <ProcessSectionHeader
                  label="Running"
                  count={activeProcesses.length}
                  isOpen={isRunningOpen}
                  onToggle={() => setIsRunningOpen((current) => !current)}
                />
                {isRunningOpen &&
                  activeProcesses.map((process) => (
                    <ProcessItem
                      key={getTransactionKey(process.type, process.id)}
                      process={process}
                    />
                  ))}
              </section>
            )}

            {recentProcesses.length > 0 && (
              <section>
                <ProcessSectionHeader
                  label="Recent"
                  count={recentProcesses.length}
                  isOpen={isRecentOpen}
                  onToggle={() => setIsRecentOpen((current) => !current)}
                />
                {isRecentOpen &&
                  recentProcesses.map((process) => (
                    <ProcessItem
                      key={getTransactionKey(process.type, process.id)}
                      process={process}
                    />
                  ))}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export const ProcessMonitorTrigger = () => {
  const processes = useProcessMonitorStore((state) => state.processes)
  const isOpen = useProcessMonitorStore((state) => state.isOpen)
  const setOpen = useProcessMonitorStore((state) => state.setOpen)
  const activeCount = Object.values(processes).filter((process) =>
    isActiveTransaction(process.status)
  ).length

  return (
    <Popover
      open={isOpen}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      content={<ProcessActivityContent />}
      overlayInnerStyle={{ padding: 0, borderRadius: 12, overflow: "hidden" }}
      overlayStyle={{ paddingTop: 10 }}
    >
      <Tooltip title="Process activity">
        <button
          type="button"
          aria-label="Open process activity"
          aria-expanded={isOpen}
          className={`flex h-8 w-8 items-center justify-center transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary500 ${
            isOpen ? "text-primary700" : "text-primary500 hover:text-primary700"
          }`}
        >
          <Badge
            count={activeCount || 0}
            size="small"
            overflowCount={9}
          >
            <ProcessMonitorIcon
              className={`h-5 w-5 text-primary500 ${
                activeCount ? "animate-spin" : ""
              }`}
            />
          </Badge>
        </button>
      </Tooltip>
    </Popover>
  )
}

export const ProcessMonitor = () => {
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const ensureOrganisation = useProcessMonitorStore(
    (state) => state.ensureOrganisation
  )
  const pruneExpiredProcesses = useProcessMonitorStore(
    (state) => state.pruneExpiredProcesses
  )

  useEffect(() => installBulkProgressInterceptors(), [])

  useEffect(() => {
    pruneExpiredProcesses()
    const cleanupTimer = window.setInterval(pruneExpiredProcesses, 60000)
    return () => window.clearInterval(cleanupTimer)
  }, [pruneExpiredProcesses])

  useEffect(() => {
    if (!isAuthenticated || !user?.userOrganisationId) {
      void stopTransactionTracking()
      return
    }

    ensureOrganisation(user?.userOrganisationId?.toString())
    void restoreTransactionTracking()

    return () => {
      void stopTransactionTracking()
    }
  }, [ensureOrganisation, isAuthenticated, user?.userOrganisationId])

  return <ProcessProgressModal />
}
